import { differenceInDays } from "date-fns";
import { PlusIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  getOrganisationQueryKey,
  useGetOrganisation,
} from "@/features/organisations/api/get-organisation";
import {
  PublicOrganisationTrial,
  ReplayTypeEnum,
  TrialFeature,
} from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteTrial } from "../api/delete-trial";
import { getTrialsQueryKey, useTrials } from "../api/fetch-trials";

import { StartTrialForm } from "./start-trial.form";

export function StartTrialModal() {
  const { id } = useParams();
  const organisationId = id ?? "";
  const queryClient = useQueryClient();
  const { data: trials, isSuccess } = useTrials(organisationId);
  const [isStartTrialModalOpen, setIsStartTrialModalOpen] = useState(false);
  const { mutateAsync: deleteTrial, isPending: isDeletingTrial } =
    useDeleteTrial({
      onSuccess: (_, { trialId }) => {
        queryClient.setQueryData(
          getTrialsQueryKey(organisationId),
          (oldData: PublicOrganisationTrial[]) => {
            return oldData.filter((trial) => trial.id !== trialId);
          },
        );

        queryClient.refetchQueries({
          queryKey: getOrganisationQueryKey(organisationId),
        });
      },
    });
  const { data: organisation, isSuccess: isOrganisationSuccess } =
    useGetOrganisation({
      id: organisationId,
    });

  /**
   * Organisation already on the advanced license, so no need to show the start trial button
   */

  if (!isSuccess || !isOrganisationSuccess)
    return (
      <Button variant="ghost" disabled>
        <PlusIcon className="mr-1 size-4 text-inherit" /> Start Trial
      </Button>
    );

  const replayTrial = trials.find(
    (trial) => trial.feature === TrialFeature.REPLAY,
  );

  if (replayTrial) {
    const daysRemaining = differenceInDays(
      new Date(replayTrial.endDate),
      new Date(),
    );
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline">
          {daysRemaining === 0
            ? "Last day!"
            : `${daysRemaining} Days Remaining`}
        </Badge>

        <Button
          variant="ghost"
          disabled={isDeletingTrial}
          size="xs"
          type="button"
          onClick={() =>
            deleteTrial({
              organisationId,
              trialId: replayTrial.id,
            })
          }
        >
          <XIcon className="mr-1 size-4 text-inherit" />
          Cancel Trial
        </Button>
      </div>
    );
  }

  if (
    isOrganisationSuccess &&
    organisation.license.replay === ReplayTypeEnum.ADVANCED
  ) {
    return null;
  }

  return (
    <Dialog
      open={isStartTrialModalOpen}
      onOpenChange={setIsStartTrialModalOpen}
    >
      <DialogTrigger asChild>
        <Button variant="ghost">
          <PlusIcon className="mr-1 size-4 text-inherit" /> Start Trial
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Start Trial</DialogTitle>
          <DialogDescription>
            Enter details required to start a new trial
          </DialogDescription>
        </DialogHeader>
        <StartTrialForm handleClose={() => setIsStartTrialModalOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}
